import { ReactNode } from 'react'
import { Link, useLocation } from 'wouter'
import { useTheme } from './theme-provider'
import { useAuth } from '@/contexts/auth-context'
import { Button } from './ui/button'
import { Moon, Sun, Menu, X, LogOut, User, ChevronDown } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { cn } from '@/lib/utils'

interface LayoutProps {
  children: ReactNode
}

export function Layout({ children }: LayoutProps) {
  const { theme, setTheme } = useTheme()
  const { user, logout } = useAuth()
  const [location] = useLocation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false)
  const profileDropdownRef = useRef<HTMLDivElement>(null)

  const navigation = [
    { name: 'Browse RFPs', href: '/request-for-proposal-marketplace', icon: '•' },
    { name: 'Vendor Spotlight', href: '/vendor-spotlight', icon: '•' },
    ...(user ? [
      { name: 'Dashboard', href: '/', icon: '•' },
      { name: 'My RFPs', href: '/my-rfps', icon: '•' },
      { name: 'My Responses', href: '/my-responses', icon: '•' },
      { name: 'Watchlist', href: '/watchlist', icon: '•' },
      { 
        name: 'Profile', 
        href: '/profile', 
        icon: '•',
        hasSubmenu: true,
        submenu: [
          { name: 'User Profile', href: '/profile' },
          { name: 'Vendor Profile', href: '/vendor-profile' }
        ]
      },
    ] : [
      { name: 'How It Works', href: '/how-it-works', icon: '•' },
      { name: 'For Buyers', href: '/buyers', icon: '•' },
      { name: 'For Vendors', href: '/vendors', icon: '•' },
      { name: 'Pricing', href: '/pricing', icon: '•' },
    ])
  ]

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const isActive = (href: string) => {
    if (href === '/') {
      return location === '/'
    }
    return location.startsWith(href)
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-gray-200/60 bg-white/95 backdrop-blur-md supports-[backdrop-filter]:bg-white/80 shadow-sm">
        <div className="container flex h-16 items-center justify-between px-4">
          {/* Logo */}
          <Link href="/" className="flex items-center space-x-3 group">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
              <span className="text-white font-bold text-sm">SI</span>
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent">
              SourcedIn
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigation.map((item) => (
              <div key={item.name} className="relative" ref={item.hasSubmenu ? profileDropdownRef : null}>
                {item.hasSubmenu ? (
                  <button
                    onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                    className={cn(
                      'flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-gray-100',
                      (isActive(item.href) || isActive('/vendor-profile'))
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'text-gray-600 hover:text-gray-900'
                    )}
                  >
                    <span className="text-xs text-gray-400">{item.icon}</span>
                    <span>{item.name}</span>
                    <ChevronDown className="w-3 h-3" />
                  </button>
                ) : (
                  <Link
                    href={item.href}
                    className={cn(
                      'flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-gray-100',
                      isActive(item.href)
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'text-gray-600 hover:text-gray-900'
                    )}
                  >
                    <span className="text-xs text-gray-400">{item.icon}</span>
                    <span>{item.name}</span>
                  </Link>
                )}
                
                {/* Profile Dropdown */}
                {item.hasSubmenu && isProfileDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    {item.submenu?.map((subItem) => (
                      <Link
                        key={subItem.name}
                        href={subItem.href}
                        onClick={() => setIsProfileDropdownOpen(false)}
                        className={cn(
                          'flex items-center px-4 py-2 text-sm transition-colors',
                          isActive(subItem.href)
                            ? 'bg-green-50 text-green-700'
                            : 'text-gray-700 hover:bg-gray-100'
                        )}
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center space-x-3">
            {/* User info */}
            {user && (
              <div className="hidden md:flex items-center space-x-3 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200">
                <div className="flex items-center space-x-2">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-900">{user.name}</span>
                    <span className="text-xs text-gray-500 truncate max-w-32">
                      {user.companyName || user.company}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Auth buttons for non-authenticated users */}
            {!user && (
              <div className="hidden md:flex items-center space-x-2">
                <Link href="/login">
                  <Button variant="ghost" size="sm" className="text-gray-600 hover:text-gray-900">
                    Login
                  </Button>
                </Link>
                <Link href="/register">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white">
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}

            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-lg hover:bg-gray-100"
              onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
            >
              <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
              <span className="sr-only">Toggle theme</span>
            </Button>

            {/* Logout button */}
            {user && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-lg hover:bg-red-50 hover:text-red-600"
                onClick={logout}
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden h-9 w-9 rounded-lg hover:bg-gray-100"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white/95 backdrop-blur-md">
            <nav className="container py-4 space-y-1">
              {navigation.map((item) => (
                <div key={item.name}>
                  {item.hasSubmenu ? (
                    <div>
                      <button
                        onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                        className={cn(
                          'flex items-center justify-between w-full px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200',
                          (isActive(item.href) || isActive('/vendor-profile'))
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        )}
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-xs text-gray-400">{item.icon}</span>
                          <span>{item.name}</span>
                        </div>
                        <ChevronDown className={cn("w-4 h-4 transition-transform", isProfileDropdownOpen && "rotate-180")} />
                      </button>
                      {isProfileDropdownOpen && (
                        <div className="ml-6 space-y-1 mt-1">
                          {item.submenu?.map((subItem) => (
                            <Link
                              key={subItem.name}
                              href={subItem.href}
                              className={cn(
                                'flex items-center space-x-3 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                                isActive(subItem.href)
                                  ? 'bg-green-50 text-green-700 border border-green-200'
                                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                              )}
                              onClick={() => {
                                setIsMobileMenuOpen(false)
                                setIsProfileDropdownOpen(false)
                              }}
                            >
                              <span className="text-xs text-gray-400">•</span>
                              <span>{subItem.name}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      href={item.href}
                      className={cn(
                        'flex items-center space-x-3 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200',
                        isActive(item.href)
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      )}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span className="text-xs text-gray-400">{item.icon}</span>
                      <span>{item.name}</span>
                    </Link>
                  )}
                </div>
              ))}
              {!user && (
                <div className="pt-4 border-t border-gray-200 space-y-2">
                  <Link
                    href="/login"
                    className="flex items-center space-x-3 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="text-xs text-gray-400">•</span>
                    <span>Login</span>
                  </Link>
                  <Link
                    href="/register"
                    className="flex items-center space-x-3 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 bg-green-600 text-white hover:bg-green-700"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="text-xs text-green-200">•</span>
                    <span>Sign Up</span>
                  </Link>
                </div>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1">
        <div className="container py-8">
          {children}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t">
        <div className="container py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
            {/* Company */}
            <div className="col-span-2">
              <Link href="/" className="flex items-center space-x-2 mb-4">
                <div className="h-8 w-8 rounded-lg gradient-bg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">SI</span>
                </div>
                <span className="text-xl font-bold text-gradient">SourcedIn</span>
              </Link>
              <p className="text-sm text-muted-foreground mb-4 max-w-sm">
                The world's leading RFP marketplace connecting buyers and vendors through intelligent procurement solutions.
              </p>
              <div className="flex space-x-4">
                <a href="https://www.linkedin.com/company/sourcedin-ai/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary">
                  <span className="sr-only">LinkedIn</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a href="#" className="text-muted-foreground hover:text-primary">
                  <span className="sr-only">Twitter</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Product */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Product</h3>
              <ul className="space-y-3">
                <li><Link href="/features" className="text-sm text-muted-foreground hover:text-primary">Features</Link></li>
                <li><Link href="/pricing" className="text-sm text-muted-foreground hover:text-primary">Pricing</Link></li>
                <li><Link href="/security" className="text-sm text-muted-foreground hover:text-primary">Security</Link></li>
                <li><Link href="/request-for-proposal-marketplace" className="text-sm text-muted-foreground hover:text-primary">Browse RFPs</Link></li>
              </ul>
            </div>

            {/* Solutions */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Solutions</h3>
              <ul className="space-y-3">
                <li><Link href="/buyers" className="text-sm text-muted-foreground hover:text-primary">For Buyers</Link></li>
                <li><Link href="/vendors" className="text-sm text-muted-foreground hover:text-primary">For Vendors</Link></li>
                <li><Link href="/industries" className="text-sm text-muted-foreground hover:text-primary">Industries</Link></li>
                <li><Link href="/how-it-works" className="text-sm text-muted-foreground hover:text-primary">How It Works</Link></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Company</h3>
              <ul className="space-y-3">
                <li><Link href="/about-us" className="text-sm text-muted-foreground hover:text-primary">About Us</Link></li>
                <li><Link href="/success-stories" className="text-sm text-muted-foreground hover:text-primary">Success Stories</Link></li>
                <li><Link href="/blog" className="text-sm text-muted-foreground hover:text-primary">Blog</Link></li>
                <li><Link href="/contact" className="text-sm text-muted-foreground hover:text-primary">Contact</Link></li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex space-x-6 mb-4 md:mb-0">
                <Link href="/privacy" className="text-sm text-muted-foreground hover:text-primary">Privacy Policy</Link>
                <Link href="/terms" className="text-sm text-muted-foreground hover:text-primary">Terms of Service</Link>
              </div>
              <p className="text-sm text-muted-foreground">
                © 2025 SourcedIn. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
} 